// Contracts shared between the client, the API and the game server: zod schemas (validated on both sides) and real-time protocol.
import { z } from 'zod/v4';
import { OAuthProvider } from './oauth.js';
import { Role } from './admin.js';
import { Body, ModeRulesSchema, PlanAdjust, TrackDefSchema, Wear, WorkoutContent } from './creations.js';
import { Country } from './countries.js';

export * from './creations.js';
export * from './oauth.js';
export * from './admin.js';
export * from './clubs.js';
export * from './countries.js';

// ---------- Comptes ----------
export const Email = z.string().trim().toLowerCase().email().max(200);
export const Password = z.string().min(8, 'Password: at least 8 characters').max(200);
export const DisplayName = z.string().trim().min(1).max(20);

/**
 * Version of the terms of use and privacy policy (date of the text). Bump it when the texts change in a way that needs a
 * new acceptance: every account whose `termsVersion` differs is asked again before using the app (docs/LEGAL.md).
 */
export const TERMS_VERSION = '2026-11-04';
/**
 * Figures the terms and the privacy policy state about the system, filled into the texts as `{placeholders}` and
 * checked against the code and the infrastructure by tests (apps/api/test/legal.test.ts, apps/web/test/legal.test.ts):
 * a retention that changes in the code without changing here fails the build. Changing a figure is a material change
 * of the texts: bump TERMS_VERSION with it.
 */
export const LEGAL_FACTS = {
  /** Minimum age to hold an account (GDPR default; Belgium would allow 13). */
  minAge: 16,
  /** Access token lifetime (auth.ts ACCESS_TTL_S). */
  accessMinutes: 15,
  /** Refresh token lifetime (auth.ts REFRESH_TTL_MS). */
  refreshDays: 30,
  /** Temporary block after failed sign-ins (security.ts LOGIN_BLOCK_MS). */
  loginBlockMinutes: 15,
  /** Replays kept per player (routes/replays.ts REPLAY_KEEP). */
  replayKeep: 20,
  /** Lifetime of a password reset link (routes/password.ts RESET_TTL_MS). */
  resetMinutes: 60,
  /** Server logs retention (infra/azure/main.bicep retentionInDays). */
  logDays: 30,
  /** Database backups retention (infra/azure/main.bicep backupRetentionDays). */
  backupDays: 7,
  /** Inactivity after which an account may be deleted, with notice (announced; no job does it yet). */
  inactivityYears: 3,
  /** Shortest abandoned recording kept as an unfinished activity (routes/recordings.ts RECORDING_MIN_S). */
  recordingMinSeconds: 30,
} as const;
/** Sign-up records the acceptance of the terms: an account is never created without it. */
export const RegisterBody = z.object({ email: Email, password: Password, name: DisplayName, acceptTerms: z.literal(true) });
/** Acceptance of the current terms by an existing account (sign-up through Google or Strava, or terms that changed). */
export const ConsentBody = z.object({ version: z.literal(TERMS_VERSION) });
/** Who runs this deployment (legal notice and privacy policy), configured by the operator; public. */
export const LegalInfo = z.object({
  operator: z.string(), address: z.string(), email: z.string(), hosting: z.string(),
  /** False until the operator set the LEGAL_* variables: the pages then say the notice is incomplete. */
  configured: z.boolean(), termsVersion: z.string(),
});
export type LegalInfo = z.infer<typeof LegalInfo>;
export const LoginBody = z.object({ email: Email, password: z.string().min(1).max(200) });
/** Forgotten password: the server answers the same whether the address has an account or not. `lang` picks the e-mail's language. */
export const ForgotPasswordBody = z.object({ email: Email, lang: z.enum(['en', 'fr']).optional() });
/** The e-mailed link carries the token; the new password replaces the old one, closes every other session and opens this one. */
export const ResetPasswordBody = z.object({ token: z.string().min(20).max(200), password: Password });
export const RefreshBody = z.object({ refreshToken: z.string().min(20).max(200) });
// null email: account created through Strava, which does not share the address
// termsVersion: the terms this account accepted (null: not yet, e.g. created through Google or Strava); absent in older responses
export const PublicUser = z.object({ id: z.string(), email: z.string().nullable(), name: z.string(), createdAt: z.number(), friendCode: z.string().optional(), termsVersion: z.string().nullable().optional(),
  /** The account's role (ADR 0007): only an administrator may set watts with the keyboard, to test without a trainer. */
  role: Role.optional() });
export const AuthResponse = z.object({ user: PublicUser, accessToken: z.string(), refreshToken: z.string(), expiresIn: z.number() });
/** End of an external sign-in flow: a session (`created`: brand-new account), or a linked account. */
export const OAuthFinishResponse = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('session'), session: AuthResponse, created: z.boolean() }),
  z.object({ kind: z.literal('linked'), provider: OAuthProvider }),
]);
export type OAuthFinishResponse = z.infer<typeof OAuthFinishResponse>;
export type PublicUser = z.infer<typeof PublicUser>;
export type AuthResponse = z.infer<typeof AuthResponse>;

// ---------- Culture (langue, fuseau, horloge) ----------
/** Interface languages. Must stay identical to `LANGS` of @gruvetto/core (checked by a compatibility test). */
export const LANG_CODES = ['en', 'fr'] as const;
const knownTimeZone = (tz: string) => { try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch { return false; } };
export const TimeZone = z.string().trim().min(1).max(64).refine(knownTimeZone, 'Unknown time zone');
const cultureShape = { lang: z.enum(['auto', ...LANG_CODES]), timeZone: TimeZone, autoTimeZone: z.boolean(), clock: z.enum(['auto', 'h12', 'h23']) };
export const CulturePrefs = z.object({
  lang: cultureShape.lang.default('auto'),
  timeZone: cultureShape.timeZone.default('UTC'),
  autoTimeZone: cultureShape.autoTimeZone.default(true),
  clock: cultureShape.clock.default('auto'),
});
export type CulturePrefs = z.infer<typeof CulturePrefs>;
export const WeekLeaderboardQuery = z.object({ tz: TimeZone.optional() });

// ---------- Profil, progression ----------
/** Outfit: official patterns by id, worn created cosmetics (embedded), body and face. */
const lookShape = { pattern: z.string().max(20), frame: z.string().max(20), helmet: z.string().max(20), wear: Wear, body: Body };
export const Look = z.object({
  pattern: lookShape.pattern.default('uni'), frame: lookShape.frame.default('couleur'), helmet: lookShape.helmet.default('couleur'),
  wear: Wear.optional(), body: Body.optional(),
});
export type Look = z.infer<typeof Look>;
/** The profile the player edits, as stored. The FTP is not in it: the server estimates it from the rides (ADR 0027). */
export const PlayerProfile = z.object({
  name: DisplayName,
  mass: z.number().min(35).max(150),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  look: Look,
  culture: CulturePrefs.prefault({}),
  /** The country the player chose to ride for, shown as a flag next to their name in races; null or absent: none. */
  country: Country.nullish(),
});
export type PlayerProfile = z.infer<typeof PlayerProfile>;
/** The profile as the API shows it: the stored one with the FTP the server estimated. */
export const Profile = PlayerProfile.extend({ ftp: z.number().int().min(60).max(600) });
/**
 * The server's FTP estimate (core ftp.ts): its value, whether it is still the weight-based start, how it moved, and
 * why a test would help (null: the rides say enough).
 */
export const FtpState = z.object({
  ftp: z.number().int().min(60).max(600),
  provisional: z.boolean(),
  basis: z.enum(['seed', 'efforts', 'test']),
  testDue: z.enum(['unknown', 'stale', 'return', 'struggling']).nullable(),
  history: z.array(z.object({ date: z.number(), ftp: z.number().int(), from: z.number().int() })).max(200),
});
export type FtpState = z.infer<typeof FtpState>;
/** Partial update: `culture` and `look` also contain only the modified fields
 * (shapes without default values: in zod v4, `.partial()` would fill in the defaults and overwrite the profile).
 * Strict: an FTP sent by a client is refused, never silently dropped. */
export const ProfilePatch = PlayerProfile.extend({ culture: z.object(cultureShape).partial(), look: z.object(lookShape).partial() }).partial().strict();
export const Totals = z.object({ rides: z.number(), km: z.number(), hours: z.number(), elevation: z.number(), wins: z.number(), hits: z.number(), workouts: z.number(), tests: z.number() });
/** Legendary challenge progress (core challenges.ts), recomputed by the API from the samples. */
const ChallengeAttempt = z.object({ start: z.number().finite(), last: z.number().finite(), climb: z.number().min(0), time: z.number().min(0), distance: z.number().min(0), rides: z.number().int().min(0) });
export const ChallengeRecord = z.object({ attempt: ChallengeAttempt.nullable(), completions: z.number().int().min(0), best: z.number().min(0).nullable() });
export const ChallengeState = z.record(z.string().max(40), ChallengeRecord).refine(o => Object.keys(o).length <= 50, 'Too many challenges');
export const Progress = z.object({ xp: z.number().int().min(0), achievements: z.array(z.string().max(40)).max(200), coins: z.number().int().min(0).optional(), challenges: ChallengeState.optional() });
export const PlanState = z.object({ id: z.string().max(60), start: z.number(), done: z.record(z.string(), z.string()), completedAt: z.number().optional(), total: z.number().int().min(1).max(500).optional(), adjust: PlanAdjust.optional() }).nullable();
/** The player's club, as every race shows it (the tag next to the name) and as team modes group riders by it. */
export const MyClubRef = z.object({ id: z.string(), name: z.string(), tag: z.string() }).nullable();
export type MyClubRef = z.infer<typeof MyClubRef>;
export const MeResponse = z.object({ user: PublicUser, profile: Profile, ftp: FtpState, progress: Progress, totals: Totals, plan: PlanState, club: MyClubRef.optional() });
export type Profile = z.infer<typeof Profile>;
export type MeResponse = z.infer<typeof MeResponse>;

// ---------- Gruvetto score (ADR 0009) ----------
/** Event category identifiers: stored as letters, shown as score ranges. */
export const CategoryId = z.enum(['A', 'B', 'C', 'D', 'E']);
/** The player's score, computed by the server only (GET /rating). */
export const RatingResponse = z.object({
  score: z.number().min(0).max(1000),
  power: z.number().min(0).max(1000),
  races: z.number().int().min(0),
  provisional: z.boolean(),
  category: CategoryId,
});
export type RatingResponse = z.infer<typeof RatingResponse>;

// ---------- Activities ----------
const nums = (max: number) => z.array(z.number().finite()).max(max);
/** Longest activity, in 1 Hz samples: a day (the same figure as core's MAX_ACTIVITY_S, checked by apps/api/test). */
export const MAX_ACTIVITY_SAMPLES = 24 * 3600;
/** Largest slice of samples one request appends to a recording in progress (ten minutes, about 40 KB of JSON). */
export const RECORDING_CHUNK = 600;
const samplesOf = (max: number) => z.object({ t: nums(max), p: nums(max), c: nums(max), hr: nums(max), v: nums(max), d: nums(max), alt: nums(max) });
export const Samples = samplesOf(MAX_ACTIVITY_SAMPLES);
export type Samples = z.infer<typeof Samples>;
/** What the server noticed on a ride: its power is out of human reach, or it overlaps another ride of the player. */
export const ACTIVITY_FLAGS = ['power', 'overlap'] as const;
export type ActivityFlag = (typeof ACTIVITY_FLAGS)[number];
/** A receipt holds a full field (fifty riders) and an event stage, in base64. */
export const RECEIPT_MAX_CHARS = 128 * 1024;
export const ActivityMeta = z.object({
  type: z.enum(['race', 'workout', 'game']), name: z.string().max(120), mode: z.string().max(60).nullish(), track: z.string().max(60).nullish(),
  rank: z.number().int().min(1).max(64).nullish(), riders: z.number().int().max(64).nullish(), hits: z.number().int().min(0).max(500).optional(),
  online: z.boolean().optional(), workoutId: z.string().max(60).nullish(), completed: z.boolean().optional(), test: z.enum(['ftp20', 'ramp']).nullish(),
  ghostRecord: z.boolean().optional(), time: z.number().nullish(), planKey: z.string().max(20).nullish(), compliance: z.number().min(0).max(100).nullish(),
  eventId: z.string().max(60).nullish(), stage: z.number().int().min(0).max(30).nullish(), cat: z.string().max(2).nullish(),
  creationId: z.string().max(60).nullish(), score: z.number().finite().min(-1e9).max(1e9).nullish(),
  power: z.enum(['measured', 'virtual', 'simulated']).nullish(),
  challenge: z.string().max(40).nullish(),
  /** Ghost run: the record's key, which the server checks a claimed record against. */
  ghostKey: z.string().max(80).nullish(),
  /** The game server's signed results of an online race (ADR 0022). Read at the save, never stored. */
  receipt: z.string().max(RECEIPT_MAX_CHARS).optional(),
  /** Written by the server alone: whatever a client sends here is replaced. `verified`: the results come from a signed receipt. */
  verified: z.boolean().optional(), raceId: z.string().max(40).optional(), flags: z.array(z.enum(ACTIVITY_FLAGS)).max(4).optional(),
  lang: z.enum(LANG_CODES).optional(),
});
const ActivityId = z.string().regex(/^[a-z0-9-]{4,40}$/i);
export const CreateActivityBody = z.object({ id: ActivityId.optional(), date: z.number().optional(), meta: ActivityMeta, samples: Samples });
export type ActivityMeta = z.infer<typeof ActivityMeta>;
export type CreateActivityBody = z.infer<typeof CreateActivityBody>;

/**
 * Recording in progress (ADR 0019): the client opens it when the ride starts, appends its samples every couple of
 * minutes, and closes it with the final metadata; the server then stores the activity and computes the rewards.
 * `meta` at opening is provisional: it names the ride if the client never comes back to close it.
 */
/** The workout of a group workout room: each rider's targets come from their own FTP. `id` and `name` are only displayed and recorded. */
export const RoomWorkout = WorkoutContent.extend({ id: z.string().min(1).max(60), name: z.string().trim().min(1).max(80), desc: z.string().max(400).optional() });
export type RoomWorkout = z.infer<typeof RoomWorkout>;

/**
 * How a solo ride was set up, kept with its recording so the ride can be resumed after the page closed (ADR 0028).
 * Only what rebuilds the session: a mode, a track or an item is an id the catalogue resolves again; a workout travels
 * whole, since a plan or the coach may have generated it. Online rides have none: the room holds their state.
 */
const SetupId = z.string().min(1).max(80);
export const RideSetup = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('race'), mode: SetupId, track: SetupId,
    laps: z.number().int().min(1).max(50).optional(), bots: z.number().int().min(0).max(50).optional(), diff: z.string().max(20).optional(),
    groupWkg: z.number().min(0.5).max(8).optional(), elastic: z.boolean().optional(),
    items: z.array(SetupId).max(12).optional(), itemsOff: z.array(z.string().max(40)).max(40).optional(),
  }),
  z.object({ kind: z.literal('workout'), track: SetupId, workout: RoomWorkout, planKey: z.string().max(40).nullable(), intensity: z.number().min(0.5).max(1.5) }),
  z.object({ kind: z.literal('challenge'), id: SetupId }),
]);
export type RideSetup = z.infer<typeof RideSetup>;
export const RecordingIdParam = z.object({ id: ActivityId });
export const OpenRecordingBody = z.object({ id: ActivityId, date: z.number().optional(), meta: ActivityMeta, setup: RideSetup.optional() });
/** `from`: how many samples the server already holds; the slice continues the recording there, or the server answers 409 with its length. */
export const AppendRecordingBody = z.object({ from: z.number().int().min(0).max(MAX_ACTIVITY_SAMPLES), samples: samplesOf(RECORDING_CHUNK) });
export const CloseRecordingBody = z.object({ meta: ActivityMeta });
export type OpenRecordingBody = z.infer<typeof OpenRecordingBody>;
export type AppendRecordingBody = z.infer<typeof AppendRecordingBody>;
export type CloseRecordingBody = z.infer<typeof CloseRecordingBody>;
/** Answer of the opening and of each append: how many samples the server holds. */
export interface RecordingState { id: string; length: number }
/** GET /recordings/open: the player's latest recording left open, whole, so the ride can go on (ADR 0028). */
export interface OpenRecording { id: string; date: number; meta: ActivityMeta; setup: RideSetup | null; samples: Samples; updatedAt: number }
export interface OpenRecordingResponse { recording: OpenRecording | null }

// ---------- Custom workouts, ghosts ----------
export const Workout = WorkoutContent.extend({ id: z.string().max(60), name: z.string().max(80), desc: z.string().max(400).optional(), custom: z.boolean().optional() });
export const GhostBody = z.object({ activityId: z.string().max(40), time: z.number().positive(), rec: z.array(z.tuple([z.number(), z.number()])).max(40000) });

// ---------- Import of local data (first sign-in) ----------
// ---------- Classements ----------
/** `tag`: the player's club tag, when they belong to one. */
/** A leaderboard row; `color` and `look` draw the player's portrait (absent from entries served before they existed). */
export const LeaderboardEntry = z.object({ rank: z.number(), userId: z.string(), name: z.string(), value: z.number(), extra: z.string().optional(), tag: z.string().optional(), color: z.string().max(20).optional(), look: z.unknown().optional() });
export type LeaderboardEntry = z.infer<typeof LeaderboardEntry>;

// ---------- Amis ----------
/** Friend code: 8 characters without visual ambiguity (no 0/O or 1/I). */
export const FRIEND_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
export const FriendCode = z.string().trim().toUpperCase().regex(/^[0-9A-Z]{6,10}$/, 'Invalid friend code');
export const FriendRequestBody = z.union([z.object({ code: FriendCode }), z.object({ userId: z.string().uuid() })]);
export const UserIdParam = z.object({ id: z.string().uuid('Invalid identifier') });
export const UserSearchQuery = z.object({ q: z.string().trim().min(2, 'Type at least 2 characters').max(40) });
export type FriendRelation = 'friend' | 'incoming' | 'outgoing' | null;
export interface FriendSummary {
  userId: string; name: string; color: string; look: unknown; xp: number;
  rides: number; km: number; lastRideAt: number | null;
  /** Date of the request or of the acceptance (ms). */
  since: number;
}
export interface FriendsResponse { code: string; friends: FriendSummary[]; incoming: FriendSummary[]; outgoing: FriendSummary[] }
/** Where a friend is right now, if they are in an online room: the room code and whether a race is on. Friends only. */
export interface FriendPresence { code: string; racing: boolean; event: boolean }
export type FriendsPresenceResponse = Record<string, FriendPresence>;
export interface UserSearchResult { userId: string; name: string; color: string; xp: number; relation: FriendRelation }
export interface FriendRequestResponse { status: 'pending' | 'accepted'; friend: { userId: string; name: string } }
export interface FeedItem { userId: string; name: string; color: string; activity: { id: string; date: number; type: string; name: string; meta: ActivityMeta; summary: Record<string, unknown>; xp: number } }

// ---------- Events and tours ----------
/** Raw definition: bounded and corrected on the server side by normalizeEvent (@gruvetto/core). */
export const EventInput = z.record(z.string(), z.unknown()).refine(v => JSON.stringify(v).length < 60000, 'Definition too large');
/** private: me only · link: anyone with the link · public: shown in everyone's calendar. */
export const EventVisibility = z.enum(['private', 'link', 'public']);
export const PutEventBody = z.object({ def: EventInput, visibility: EventVisibility.default('link') });
export const EventIdParam = z.object({ id: z.string().regex(/^[\w-]{3,60}$/, 'Invalid event identifier') });
export const StageParam = EventIdParam.extend({ stage: z.coerce.number().int().min(0).max(20) });
export const EntryBody = z.object({ cat: CategoryId });
const StageRider = z.object({
  key: z.string().max(80), name: z.string().max(40), color: z.string().max(9), human: z.boolean(), cat: z.string().max(2), rookie: z.boolean(),
  rank: z.number().int().min(1).max(64), time: z.number().nonnegative().max(36000).nullable(), dist: z.number().min(-100).max(500000), goal: z.number().nonnegative().max(500000).nullable(),
  out: z.boolean(), hits: z.number().int().min(0).max(500),
  /** Team of a team stage (absent from older clients and individual stages). */
  team: z.string().max(12).nullable().optional(),
});
/** The game server's results of a race, as it signs them (ADR 0022): only players with a verified account. */
export const RaceReceiptPayload = z.object({
  raceId: z.string().max(40), at: z.number(), mode: z.string().max(60), track: z.string().max(60), riders: z.number().int().min(1).max(64),
  results: z.array(z.object({ u: z.string().max(40), rank: z.number().int().min(1).max(64).nullable(), time: z.number().nullable(), hits: z.number().int().min(0).max(500) })).max(64),
  eventId: z.string().max(60).optional(), stage: z.unknown().optional(),
});
export type RaceReceiptPayload = z.infer<typeof RaceReceiptPayload>;
export const StageResultBody = z.object({
  stage: z.number().int().min(0).max(20), mode: z.string().max(10), elapsed: z.number().nonnegative().max(36000), date: z.number().optional(),
  riders: z.array(StageRider).min(1).max(64),
  cps: z.array(z.object({ kind: z.enum(['sprint', 'kom']), cat: z.number().int().min(0).max(4).optional(), label: z.string().max(60).optional(), order: z.array(z.string().max(80)).max(64) })).max(40),
});
export const AiEventBody = z.object({
  prompt: z.string().trim().min(3, 'Describe your event in a few words').max(2000), current: EventInput.nullish(),
  /** Player's time zone offset in minutes (getTimezoneOffset), to understand "Saturday 6 pm". */
  tzOffset: z.number().int().min(-840).max(840).optional(),
});
export type EventVisibility = z.infer<typeof EventVisibility>;
export interface EventSummary { def: Record<string, unknown>; owner: { id: string; name: string } | null; visibility: EventVisibility | 'builtin'; entries: number; myCat: string | null; mine: boolean }
/** A rider entered in an event: the look draws their portrait in the standings (null: none set). */
export interface EventEntry { userId: string; name: string; color: string; look: Look | null; cat: string; stages: number }
export interface AiEventResponse { def: Record<string, unknown>; warnings: string[]; source: 'claude' | 'local'; note?: string }

// ---------- Real-time protocol (/ws) ----------
// Every message received by the game server is validated by ClientMessageSchema. The bounds are wide: the server
// then tightens the business values (watts, mass, FTP…), the schema is there to reject whatever has the wrong shape.
/** Maximum size of an incoming WebSocket message, in bytes. */
export const WS_MAX_PAYLOAD = 16 * 1024;
const finite = z.number().finite();
export const GameAction = z.enum(['steerLeft', 'steerRight', 'fire', 'gearUp', 'gearDown']);
export type GameAction = z.infer<typeof GameAction>;
/** Cheers a supporter can send (same list as core CHEER_KINDS: contracts depend on zod alone). */
export const CheerKindSchema = z.enum(['go', 'fire', 'clap', 'horn', 'heart', 'wow']);
export type CheerKind = z.infer<typeof CheerKindSchema>;
/** What the host allows the audience (spectators) of a room: chatting, and cheers that give the crowd boost. */
export const AudienceSchema = z.object({ chat: z.boolean(), boost: z.boolean() });
export type Audience = z.infer<typeof AudienceSchema>;
export const RoomConfigSchema = z.object({
  mode: z.string().max(60), track: z.string().max(60), laps: finite, bots: finite, diff: z.string().max(20), groupWkg: finite,
  /** Created mode or track: embedded rules and layout (validated here and by the game server), `mode`/`track` carry the creation's id. */
  rules: ModeRulesSchema.optional(), trackDef: TrackDefSchema.optional(),
  /** Absent from older clients: everything allowed (the server completes it). */
  audience: AudienceSchema.partial().optional(),
  /** Riders stay together whatever their watts (free ride, group workout). Absent from older clients: off. */
  elastic: z.boolean().optional(),
  /** Workout ridden together when `mode` is 'workout'. */
  workout: RoomWorkout.optional(),
});
export type RoomConfig = z.infer<typeof RoomConfigSchema>;
/** Validation of the creations embedded in a room's configuration. */
export const RoomCreations = z.object({ rules: ModeRulesSchema.optional(), trackDef: TrackDefSchema.optional() });
/**
 * Phone remote (ADR 0027): every control a phone can press, the same list as the client's game actions
 * (apps/web devices/input.ts GAME_ACTIONS), so a phone drives a ride like any gamepad.
 */
export const RemoteAction = z.enum(['steerLeft', 'steerRight', 'fire', 'gearUp', 'gearDown', 'look', 'camera', 'pause']);
export type RemoteAction = z.infer<typeof RemoteAction>;
/** What the computer is doing, so the phone shows the matching buttons: a race, a workout, an arcade game, or nothing yet. */
export const RemoteLayout = z.enum(['idle', 'race', 'workout', 'game']);
export type RemoteLayout = z.infer<typeof RemoteLayout>;
/** Alphabet of the pairing codes typed on a phone: no 0/O, 1/I/L, so a code read off a screen is never ambiguous. */
export const REMOTE_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const REMOTE_CODE_LENGTH = 6;
const base64url = /^[A-Za-z0-9_-]+$/;
/** A pairing code (6 characters, typed), the key of a scanned QR code, or a phone's resume token (both base64url). */
const RemoteSecret = z.string().min(REMOTE_CODE_LENGTH).max(64).regex(base64url);
export const ClientMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('hello'), name: z.string().max(100), color: z.string().max(20), mass: finite, ftp: finite, look: Look.nullish(), token: z.string().max(4096).nullish(),
    /** Club tag and id the client knows (its last sync); the server replaces them by the account's own when it can look them up. */
    tag: z.string().max(4).nullish(), club: z.string().max(40).nullish(),
    /** The player's country (profile): the server keeps it only when it is a known code (COUNTRY_CODES). */
    country: z.string().max(2).nullish(),
    /** Draft assist in races (absent from older clients: on, the game's default). */
    assist: z.boolean().optional() }),
  z.object({ t: z.literal('create') }),
  z.object({ t: z.literal('join'), code: z.string().max(10) }),
  z.object({ t: z.literal('leave') }),
  /** The rider stops riding but stays in the room. */
  z.object({ t: z.literal('retire') }),
  z.object({ t: z.literal('config'), config: RoomConfigSchema.partial() }), // the server completes it with its default values
  z.object({ t: z.literal('ready'), ready: z.boolean() }),
  z.object({ t: z.literal('start') }),
  z.object({ t: z.literal('chat'), text: z.string().max(1000) }),
  /** `k`: the watts are typed on the keyboard (an administrator's test): the rider is never rated nor given a signed result. */
  z.object({ t: z.literal('in'), p: finite, c: finite, hr: finite.nullish(), k: z.boolean().optional() }),
  z.object({ t: z.literal('act'), a: GameAction }),
  z.object({ t: z.literal('ping'), c: finite }),
  /** Reattaches a new socket to a player's slot after a network drop. `auth` (access token) is required when the slot belongs to a signed-in account. */
  z.object({ t: z.literal('resume'), token: z.string().min(16).max(64), auth: z.string().max(4096).nullish() }),
  /** Joins (or opens) the room of an event stage: all registered players arrive in the same room. */
  z.object({ t: z.literal('joinEvent'), def: EventInput, stage: z.number().int().min(0).max(20), cat: z.string().max(2), rookie: z.boolean().optional() }),
  /** Watches a room as a supporter: no seat, no rider, the race as everyone sees it, the chat, and cheers. Allowed mid-race. */
  z.object({ t: z.literal('spectate'), code: z.string().max(10) }),
  /** A supporter cheers for rider `for` (index in the race). The server bounds the rate and the effect (core Race.cheer). */
  z.object({ t: z.literal('cheer'), for: z.number().int().min(0).max(255), kind: CheerKindSchema }),
  /** Host only: moderate one supporter of the room — mute/unmute their chat and cheers, or remove them. */
  z.object({ t: z.literal('spectatorAction'), id: z.string().max(40), action: z.enum(['mute', 'unmute', 'remove']) }),
  /** Phone remote, computer side (signed-in account): opens a pairing and gets a code, or takes its own back after a reload (`resume`). */
  z.object({ t: z.literal('remoteOpen'), resume: z.string().min(16).max(64).regex(base64url).optional() }),
  /** Computer side: ends the pairing; the phone is told. */
  z.object({ t: z.literal('remoteClose') }),
  /** Computer side: what it is showing, relayed to the phone so its buttons follow. */
  z.object({ t: z.literal('remoteState'), layout: RemoteLayout }),
  /** Phone side: pairs with a code, or comes back with its resume token after a drop. */
  z.object({ t: z.literal('remotePair'), code: RemoteSecret }),
  /** Phone side: a control pressed (`d`: down) or released, relayed to the paired computer. */
  z.object({ t: z.literal('remoteInput'), a: RemoteAction, d: z.boolean() }),
  /** Phone side: the player unpairs this phone. */
  z.object({ t: z.literal('remoteLeave') }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;
export interface RoomPlayer { id: string; key?: string; name: string; color: string; ftp: number; mass: number; ready: boolean; look: unknown; verified: boolean; cat?: string | null; tag?: string | null;
  /** Country code shown as a flag (absent from older servers: none). */
  country?: string | null;
  /** False while the player's connection is down and their slot is held (absent from older servers: connected). */
  connected?: boolean }
export interface RoomEvent { id: string; name: string; emoji: string; stage: number; stages: number; stageName: string; startAt: number | null }
/** A supporter watching the room (absent from older servers). `muted`: the host muted their chat and cheers. */
export interface RoomSpectator { id: string; name: string; color: string; muted?: boolean }
export interface RoomState { code: string; hostId: string; config: RoomConfig; racing: boolean; players: RoomPlayer[]; event?: RoomEvent | null; spectators?: RoomSpectator[] }
export type ServerMessage =
  | { t: 'welcome'; id: string; publicUrl: string | null }
  | { t: 'room'; room: RoomState | null } | { t: 'error'; msg: string }
  /** `spec`: the message comes from a supporter, not a rider. */
  | { t: 'chat'; from: string; color?: string; text: string; spec?: boolean }
  /** Race snapshots are not JSON messages: each one is a binary frame (core race-net-binary.ts). */
  | { t: 'start'; opts: Record<string, unknown> } | { t: 'ev'; e: unknown[] } | { t: 'end'; results: unknown[]; stage?: unknown; receipt?: string }
  /** A rider joined the race in progress (core LateSpec, with their own gun time): clients add them to their mirror after the field. */
  | { t: 'late'; human: Record<string, unknown>; id: number }
  | { t: 'pong'; c: number; s: number }
  /** Resume token of the player's slot (rotated on every resume) and how long the slot is held after a drop. */
  | { t: 'session'; token: string; graceMs: number }
  /**
   * Phone remote, to the computer: the code to show (null once a phone used it), how long it stays valid, and the
   * computer's own resume token for a page reload.
   */
  | { t: 'remoteCode'; code: string | null; ttlMs: number; token: string;
      /** Long secret of the same pairing, carried by the QR code: never typed, so never limited like the short code. */
      key: string | null }
  /** `paired`: a phone just used the code (not one coming back after a drop). */
  | { t: 'remotePeer'; phone: RemotePeerState; paired?: boolean }
  | { t: 'remoteInput'; a: RemoteAction; d: boolean }
  /** To the phone: paired with `name`'s computer; `token` brings it back after a drop. */
  | { t: 'remotePaired'; token: string; name: string; layout: RemoteLayout; host: RemoteHostState }
  | { t: 'remoteState'; layout: RemoteLayout; host: RemoteHostState }
  /**
   * The pairing is over or was refused: a wrong or expired code, closed by the computer, another phone took over, or
   * too many wrong codes typed across the server right now (`busy`: scanning the QR code still works).
   */
  | { t: 'remoteEnd'; reason: RemoteEndReason };
/** A paired phone: none, connected, or away (its connection dropped, it may come back). */
export type RemotePeerState = 'none' | 'connected' | 'away';
/** The computer seen from the phone: connected, or away for a short while (a reload, a network blip). */
export type RemoteHostState = 'connected' | 'away';
export type RemoteEndReason = 'invalid' | 'closed' | 'replaced' | 'busy';
